import { useCallback, useState } from 'react';
import * as Sentry from '@sentry/react';

import { axiosRubyClient } from '@/util/axios/axiosClient';

export interface ConversationCloseState {
  id: string;
  closed_at: string | null;
  closed_by_user_id: number | null;
  closed_by_name: string | null;
  closed_reason: string | null;
}

interface Envelope {
  success: boolean;
  data: ConversationCloseState;
  errors?: string[];
}

export function useCloseConversation() {
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = useCallback(async (conversationId: string, reason?: string) => {
    setClosing(true);
    setError(null);
    try {
      const res = await axiosRubyClient.post<Envelope>(
        `/api/admin/conversations/${conversationId}/close`,
        reason ? { reason } : {},
      );
      return res.data.data;
    } catch (err: unknown) {
      const message =
        (
          err as { response?: { data?: { errors?: string[] } } }
        )?.response?.data?.errors?.join(', ') ??
        (err as Error)?.message ??
        'Failed to close chat';
      Sentry.captureException(err, {
        tags: { feature: 'comms', action: 'close_conversation' },
        extra: { conversation_id: conversationId },
      });
      setError(message);
      throw err;
    } finally {
      setClosing(false);
    }
  }, []);

  const reopen = useCallback(async (conversationId: string) => {
    setReopening(true);
    setError(null);
    try {
      const res = await axiosRubyClient.post<Envelope>(
        `/api/admin/conversations/${conversationId}/reopen`,
      );
      return res.data.data;
    } catch (err: unknown) {
      const message =
        (
          err as { response?: { data?: { errors?: string[] } } }
        )?.response?.data?.errors?.join(', ') ??
        (err as Error)?.message ??
        'Failed to reopen chat';
      Sentry.captureException(err, {
        tags: { feature: 'comms', action: 'reopen_conversation' },
        extra: { conversation_id: conversationId },
      });
      setError(message);
      throw err;
    } finally {
      setReopening(false);
    }
  }, []);

  return { close, reopen, closing, reopening, error };
}
